import { getAvatarChar } from "lib/utils/getAvatarChar";
import { useEffect, useMemo, useState } from "react";
import { useAppStore } from "lib/store/useAppStore";
import { formatCountryLabel } from "lib/utils/countryLabels";
import { formatKoreanDateTime } from "lib/utils/koreanDateTime";
import { formatSystemRole } from "lib/utils/roleLabels";
import { PERM } from "lib/utils/permission/permissions";
import { buildDefaultPermissionByRole } from "lib/utils/permission/permissionDefaults";
import { PERM_LABELS } from "lib/utils/permission/permissionLabels";

const ROLE_RANK = { user: 1, manager: 2, admin: 3, master: 4 };
const ORGANIZATION_ROLE_PERMISSION_KEYS = new Set([
  "organization.read_all",
  "organization.read_own",
  "organization.read_children",
  PERM.ORGANIZATION_CHILD_CREATE,
  "organization.update",
  "organization.delete",
  PERM.ORGANIZATION_NOTICE_WRITE,
  "organization.user.read_all",
  "organization.user.read_own",
  "organization.user.read_children",
  "organization.user.create",
  "organization.user.update",
  "organization.user.role_update",
  "organization.user.delete",
]);

function safeObj(o) {
  return o && typeof o === "object" ? o : {};
}

function isSamePermissions(a, b) {
  const A = safeObj(a);
  const B = safeObj(b);
  const keys = new Set([...Object.keys(A), ...Object.keys(B)]);
  for (const k of keys) {
    if (Boolean(A[k]) !== Boolean(B[k])) return false;
  }
  return true;
}

function toBadgeClass(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");
  return s || "unknown";
}

function getAllowedRolesByActor(actorRole = "user") {
  const actorRank = ROLE_RANK[actorRole] ?? 1;
  return ["master", "admin", "manager", "user"].filter(
    (r) => (ROLE_RANK[r] ?? 1) <= actorRank,
  );
}

// ✅ 역할/정책에 의해 "절대 가질 수 없는" 권한을 강제 정리
function applyPermissionConstraints(role, perms) {
  const next = { ...safeObj(perms) };

  // ✅ 시스템 글로벌 공지 작성/수정 = master 고유
  if (role !== "master") next[PERM.NOTICE_WRITE_EDIT] = false;

  // ✅ user role은 사용자 등록/권한변경을 가질 수 없음
  if (role === "user") {
    next[PERM.USER_REGISTER] = false;
    next[PERM.USER_PERMISSION_EDIT] = false;
  }

  return next;
}

export default function AdminUserDetailCard({
  selectedUser,
  activities = [],
  onSaveChanges, // (id, { role, permission })
  onResetPassword,
  resettingPasswordId = null,
  onDelete, // (id)
}) {
  const user = useAppStore((s) => s.user);
  const actorRole = user?.role ?? "user";

  const [draftRole, setDraftRole] = useState("user");
  const [draftPerms, setDraftPerms] = useState({});
  const [draftUserCreateLimit, setDraftUserCreateLimit] = useState(0);
  const [autoApplyRoleDefaults, setAutoApplyRoleDefaults] = useState(true);

  const allowedRoles = useMemo(
    () => getAllowedRolesByActor(actorRole),
    [actorRole],
  );

  const canEditSelected = useMemo(() => {
    if (!selectedUser) return false;
    const targetRole = selectedUser?.role ?? "user";
    return (ROLE_RANK[targetRole] ?? 1) <= (ROLE_RANK[actorRole] ?? 1);
  }, [selectedUser, actorRole]);

  const roleOptions = useMemo(() => {
    const current = selectedUser?.role ?? "user";
    const list = allowedRoles.includes(current)
      ? [...allowedRoles]
      : [current, ...allowedRoles];

    return list.map((r) => ({
      value: r,
      label: formatSystemRole(r),
      disabled: !allowedRoles.includes(r),
    }));
  }, [allowedRoles, selectedUser?.role]);

  useEffect(() => {
    if (!selectedUser) return;
    const r = selectedUser?.role ?? "user";
    setDraftRole(r);
    setDraftPerms(applyPermissionConstraints(r, selectedUser?.permission));
    setDraftUserCreateLimit(
      Math.max(0, Number(selectedUser?.user_create_limit ?? 0) || 0),
    );
  }, [selectedUser]);

  // ✅ "안 보이게" 필터링된 권한 키 목록
  const visiblePermKeys = useMemo(() => {
    const perms = safeObj(selectedUser?.permission);
    const keys = new Set([...Object.keys(PERM_LABELS), ...Object.keys(perms)]);

    let arr = Array.from(keys);
    arr = arr.filter((key) => !ORGANIZATION_ROLE_PERMISSION_KEYS.has(key));

    // ✅ 시스템 글로벌 공지 작성/수정: master만 설정 가능
    // (그리고 대상 role이 master가 아니면 의미가 없으니 제거)
    if (actorRole !== "master" || draftRole !== "master") {
      arr = arr.filter((k) => k !== PERM.NOTICE_WRITE_EDIT);
    }

    // ✅ user role은 사용자 등록/권한변경 자체를 가질 수 없음 → user이면 리스트에서 제거
    if (draftRole === "user") {
      arr = arr.filter(
        (k) => k !== PERM.USER_REGISTER && k !== PERM.USER_PERMISSION_EDIT,
      );
    }

    return arr.sort();
  }, [selectedUser?.permission, actorRole, draftRole]);

  const hasChanges = useMemo(() => {
    if (!selectedUser) return false;
    const roleChanged = (selectedUser?.role ?? "user") !== draftRole;
    const quotaChanged =
      Math.max(0, Number(selectedUser?.user_create_limit ?? 0) || 0) !==
      draftUserCreateLimit;
    const permChanged = !isSamePermissions(
      selectedUser?.permission,
      draftPerms,
    );
    return roleChanged || permChanged || quotaChanged;
  }, [selectedUser, draftRole, draftPerms, draftUserCreateLimit]);

  const canSave = hasChanges && canEditSelected;

  const statusText = selectedUser?.active_status ?? selectedUser?.status ?? "-";
  const statusClass = toBadgeClass(statusText);

  const handleTogglePerm = (key) => {
    if (!canEditSelected) return;
    setDraftPerms((prev) =>
      applyPermissionConstraints(draftRole, {
        ...prev,
        [key]: !Boolean(prev?.[key]),
      }),
    );
  };

  const handleRoleChange = (nextRole) => {
    setDraftRole(nextRole);

    setDraftPerms((prev) => {
      const base = autoApplyRoleDefaults
        ? buildDefaultPermissionByRole(nextRole)
        : prev;
      return applyPermissionConstraints(nextRole, base);
    });
  };

  const handleSave = () => {
    if (!selectedUser || !canSave) return;

    const payload = {
      role: draftRole,
      permission: applyPermissionConstraints(draftRole, draftPerms),
      user_create_limit: draftUserCreateLimit,
    };

    if (typeof onSaveChanges === "function") {
      onSaveChanges(selectedUser.id, payload);
    }
  };

  const handleDelete = () => {
    if (!selectedUser) return;
    if (typeof onDelete === "function") onDelete(selectedUser.id);
  };

  return (
    <div className="card userDetailCard">
      <div className="cardHeader">
        <h3>사용자 상세</h3>
        <span className="hint muted">선택한 사용자의 권한과 비밀번호를 관리합니다</span>
      </div>

      {!selectedUser ? (
        <div className="emptyPanel">사용자를 선택해 주세요.</div>
      ) : (
        <>
          <div className="profileBox">
            <div className="avatarLg">
              {getAvatarChar(selectedUser?.user_name)}
            </div>

            <div className="profileText">
              <p className="pName">{selectedUser?.user_name}</p>
              <p className="pEmail muted">{selectedUser?.email}</p>

              <div className="miniRow">
                <span className="miniLabel muted">상태</span>
                <span
                  className={`badge status-${statusClass}`}
                  style={{ marginLeft: 6 }}
                >
                  {statusText}
                </span>
              </div>

              <div className="miniRow">
                <span className="miniLabel muted">최근 로그인</span>
                <span style={{ marginLeft: 6 }}>
                  {formatKoreanDateTime(selectedUser?.last_login, {
                    year: "numeric",
                  })}
                </span>
              </div>

              <div className="miniRow">
                <span className="miniLabel muted">최근 활동</span>
                <span style={{ marginLeft: 6 }}>
                  {formatKoreanDateTime(selectedUser?.last_activity_at, {
                    year: "numeric",
                  })}
                </span>
              </div>

              <div className="miniRow">
                <span className="miniLabel muted">국가</span>
                <span style={{ marginLeft: 6 }}>
                  {formatCountryLabel(selectedUser)}
                </span>
              </div>

              <div className="miniRow">
                <span className="miniLabel muted">가입일</span>
                <span style={{ marginLeft: 6 }}>
                  {formatKoreanDateTime(selectedUser?.created_at, {
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="divider" />

          {actorRole === "master" && typeof onResetPassword === "function" ? (
            <>
              <div className="miniCard">
                <div className="miniHeader">
                  <h4>비밀번호 초기화</h4>
                  <button
                    type="button"
                    className="roleAdminSecondaryButton"
                    disabled={resettingPasswordId !== null}
                    onClick={() => onResetPassword(selectedUser)}
                  >
                    {resettingPasswordId === selectedUser.id
                      ? "초기화 중..."
                      : "비밀번호 초기화"}
                  </button>
                </div>
                <p className="muted">
                  비밀번호 분실 시 초기 비밀번호 123으로 변경하고 기존 접속을 종료합니다.
                  구성원에게 다시 로그인한 뒤 비밀번호를 변경하도록 안내해 주세요.
                </p>
              </div>
              <div className="divider" />
            </>
          ) : null}

          <div className="formGrid">
            <div className="field">
              <p className="label muted">권한</p>

              <select
                className="select w100"
                value={draftRole}
                disabled={!canEditSelected}
                onChange={(e) => handleRoleChange(e.target.value)}
                title={
                  canEditSelected
                    ? ""
                    : "권한이 없어 이 사용자를 수정할 수 없습니다"
                }
              >
                {roleOptions.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                    {opt.disabled ? " (권한 없음)" : ""}
                  </option>
                ))}
              </select>

              <label
                className="muted"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                  fontSize: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={autoApplyRoleDefaults}
                  disabled={!canEditSelected}
                  onChange={(e) => setAutoApplyRoleDefaults(e.target.checked)}
                />
                역할 변경 시 기본 권한 자동 적용
              </label>
            </div>

            <div className="field">
              <p className="label muted">기능별 권한</p>

              <div className="quotaControl" style={{ marginBottom: 12 }}>
                <p className="label muted">생성 한도</p>
                <input
                  className="input w100"
                  disabled={
                    !canEditSelected ||
                    (draftRole !== "admin" && draftRole !== "manager")
                  }
                  min="0"
                  onChange={(event) =>
                    setDraftUserCreateLimit(
                      Math.max(0, Number(event.target.value) || 0),
                    )
                  }
                  type="number"
                  value={draftUserCreateLimit}
                />
                <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                  생성됨 {selectedUser?.user_create_used ?? 0}명 / 잔여{" "}
                  {selectedUser?.user_create_remaining ?? 0}명
                </p>
              </div>

              <div className="permList">
                {visiblePermKeys.map((key) => {
                  const checked = Boolean(draftPerms?.[key]);
                  const label = PERM_LABELS[key] ?? key;

                  return (
                    <label className="permItem" key={key}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canEditSelected}
                        onChange={() => handleTogglePerm(key)}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>

              {!canEditSelected ? (
                <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                  이 사용자의 정보를 수정할 권한이 없습니다.
                </p>
              ) : null}
            </div>
          </div>

          <div className="divider" />

          <div className="miniCard">
            <div className="miniHeader">
              <h4>최근 활동</h4>
              <span className="muted">최신 4건</span>
            </div>

            <ul className="activityList">
              {activities.slice(0, 4).map((a, idx) => (
                <li className="activityItem" key={idx}>
                  <div className="dot" />
                  <div className="activityBody">
                    <div className="row">
                      <span className="actTitle">
                        <b>{a.user ?? a.user_name ?? "-"}</b> ·{" "}
                        {a.log_action ?? "-"}
                      </span>
                      <span className="muted">
                        {formatKoreanDateTime(a.log_created_at, {
                          sourceTimeZone: "kst",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <p className="muted">
                      {a.log_text ?? a.log_message ?? "-"}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="divider" />

          <div className="dangerZone">
            <button
              className={`primaryBtn ${canSave ? "" : "isDisabled"}`}
              disabled={!canSave}
              onClick={handleSave}
              title={
                !canEditSelected
                  ? "권한이 없어 수정할 수 없습니다"
                  : hasChanges
                    ? "변경 사항 저장"
                    : "변경 사항이 없습니다"
              }
            >
              변경 사항 저장
            </button>

            <button className="dangerBtn" onClick={handleDelete}>
              삭제
            </button>
          </div>
        </>
      )}
    </div>
  );
}
